import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate, shiftDate } from '@/lib/date';
import { clampDayIndex, dayIndexBounds } from '@/lib/timeline-geometry';
import { dayText } from '@/components/TimelineItem';

/**
 * Which day the axis below is drawing, and the way to another one.
 *
 * The timeline shows one calendar day, midnight to midnight, and this is the
 * only thing on the screen that says which. It replaced the dated midnight
 * dividers the axis used to draw inside itself, and the reason is that a
 * divider can only name a day the canvas already holds — so the axis had to
 * grow to hold one, an ordinary 2:30 AM reception making a 48-hour, 5760px
 * canvas with most of a day of empty night in it. Naming the day *outside* the
 * canvas is what lets the canvas be a day.
 *
 * It owns no state. `dayIndex` counts calendar days from the wedding's own date
 * and belongs to the axis, which has to move it from inside a drag that carries
 * a block past midnight.
 */
export default function TimelineDateSelector({
  dayIndex,
  weddingDate,
  startTime,
  onChange,
}: {
  dayIndex: number;
  /**
   * The wedding's own calendar date, which the label is named from. Null is
   * ordinary — a planner can have hours before they have a date — and the
   * label falls back to naming days relative to the wedding's.
   */
  weddingDate: string | null;
  /**
   * The wedding's start time, which decides how far the arrows reach: which
   * days the schema's own `±MAX_OFFSET_MINUTES` can hold depends on how far
   * into its date the wedding begins.
   */
  startTime: string | null;
  onChange: (dayIndex: number) => void;
}) {
  const [first, last] = dayIndexBounds(startTime);
  const date = shiftDate(weddingDate, dayIndex);

  return (
    <div className="mb-2 flex items-center justify-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Previous day"
        disabled={dayIndex <= first}
        onClick={() => onChange(clampDayIndex(startTime, dayIndex - 1))}
      >
        <ChevronLeft />
      </Button>
      {/* Announced, because paging replaces the whole canvas beneath it: a
          screen reader left to infer the change would hear it only from
          whichever block happened to be read next.

          `tabular-nums` so the dates do not shift sideways by a digit as the
          planner pages, and a floor on the width so the arrows themselves hold
          still under a label that runs from "Sat, Jun 12" to "2 days later".
          `foreground/70` measures 4.97:1 on this card — the same step the day
          dividers used, and it clears the floor where `muted-foreground` is
          spent on the panel's own heading above. */}
      <span
        data-testid="timeline-day-label"
        aria-live="polite"
        className="label-caps min-w-32 text-center text-[0.6875rem] tabular-nums text-foreground/70"
      >
        {date
          ? formatDate(date, { weekday: 'short', month: 'short', day: 'numeric' })
          : /* The blocks' own words, with one answer added rather than
               restated: `dayText` returns null on the wedding's own day because
               a block there needs no marker, and a header does — it is the one
               thing on screen saying which day this is. */
            (dayText(dayIndex) ?? 'Wedding day')}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Next day"
        disabled={dayIndex >= last}
        onClick={() => onChange(clampDayIndex(startTime, dayIndex + 1))}
      >
        <ChevronRight />
      </Button>
    </div>
  );
}
